import React, { useState, useCallback, useEffect } from 'react';
import { Search, Database, DollarSign, BookOpen, BarChart3, Users, Zap, Rocket } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
// Using PokemonCard type instead of Card from SDK due to import issues
type Card = any;
import { searchAllCards, getMostValuableCards, getMostLikedCards, getAllSets, getSetCards } from '../services/pokemonTcgApi';
import SearchResults from '../components/SearchResults';
import debounce from 'lodash/debounce';
import { useTypingEffect } from '../hooks/useTypingEffect';
import LoadingScreen from '../components/LoadingScreen';
import { slugify } from '../utils/slugify';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { getCardPrice } from '../utils/cardPricing';
import { EnergyIcon } from '../components/icons/EnergyIcon';
import CardImage from '../components/CardImage';
import { Helmet } from 'react-helmet-async';
import { PokeverseIconAnimated } from '../components/icons/PokeverseIcon';

const SEARCH_HISTORY_KEY = 'pokemonTcgSearchHistory';

const FeatureIcons = {
  Database: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" className="stroke-blue-500" strokeWidth="2">
        <animate
          attributeName="r"
          values="10;11;10"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <path
        d="M6 12h12M12 6v12"
        className="stroke-blue-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,24;24,0;0,24"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  DollarSign: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
        className="stroke-yellow-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,48;48,0;0,48"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  BookOpen: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"
        className="stroke-green-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="d"
          values="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z;M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
      <path
        d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"
        className="stroke-green-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="d"
          values="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z;M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  BarChart: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M18 20V10M12 20V4M6 20v-6"
        className="stroke-purple-500"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,24;24,0;0,24"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  Zap: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
        className="stroke-red-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,48;48,0;0,48"
          dur="2s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),
  Rocket: () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <path
        d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
        className="stroke-orange-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,24;24,0;0,24"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
      <path
        d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22 22 0 0 1-4 2z"
        className="stroke-orange-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0,48;48,0;0,48"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  )
};

// Pokéverse Banner Component with typing effect
function PokeverseBanner() {
  const typingText = useTypingEffect([
    "Explore the Pokéverse...",
    "Discover all series...",
    "Journey through time...",
  ], 80, 40, 2500);

  // Generate stable star positions
  const stars = React.useMemo(() => 
    Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: 5 + (i * 7) % 90,
      top: 5 + (i * 11) % 90,
      size: 1 + (i % 3) * 0.5,
      opacity: 0.3 + (i % 5) * 0.15,
      duration: 2 + (i % 4),
      delay: (i % 5) * 0.4,
    })), []
  );

  return (
    <Link 
      to="/pokeverse"
      className="relative group flex flex-col items-center justify-center rounded-xl p-4 border border-white/10 hover:border-purple-500/50 transition-all duration-500 overflow-hidden"
    >
      {/* Sky gradient background - same as Pokéverse */}
      <div 
        className="absolute inset-0 rounded-xl"
        style={{
          background: `
            radial-gradient(ellipse at 80% 20%, rgba(30, 58, 138, 0.4) 0%, transparent 50%),
            radial-gradient(ellipse at 20% 80%, rgba(88, 28, 135, 0.3) 0%, transparent 50%),
            linear-gradient(to bottom, 
              #0a0a0f 0%, 
              #0d1117 30%,
              #111827 60%,
              #0f172a 100%
            )
          `,
        }}
      />
      
      {/* Moon */}
      <div className="absolute top-2 right-2 opacity-70">
        <svg width="40" height="40" viewBox="0 0 100 100">
          <defs>
            <radialGradient id="moonGradientMini" cx="30%" cy="30%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="50%" stopColor="#e2e8f0" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </radialGradient>
            <filter id="moonGlowMini" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <circle cx="50" cy="50" r="42" fill="rgba(226, 232, 240, 0.1)" />
          <circle cx="50" cy="50" r="38" fill="rgba(226, 232, 240, 0.15)" />
          <circle cx="50" cy="50" r="32" fill="url(#moonGradientMini)" filter="url(#moonGlowMini)" />
          <circle cx="35" cy="40" r="5" fill="rgba(148, 163, 184, 0.3)" />
          <circle cx="55" cy="35" r="3" fill="rgba(148, 163, 184, 0.25)" />
          <circle cx="45" cy="55" r="6" fill="rgba(148, 163, 184, 0.2)" />
          <circle cx="60" cy="52" r="4" fill="rgba(148, 163, 184, 0.25)" />
        </svg>
      </div>
      
      {/* Animated stars */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <filter id="starGlowMini" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {stars.map((star) => (
          <circle
            key={star.id}
            cx={`${star.left}%`}
            cy={`${star.top}%`}
            r={star.size}
            fill="white"
            filter="url(#starGlowMini)"
            style={{
              opacity: star.opacity,
              animation: `twinkleStar ${star.duration}s ease-in-out infinite`,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}
      </svg>
      
      {/* Center logo */}
      <div className="relative z-10 flex flex-col items-center">
        <div className="w-20 h-20 mb-2 group-hover:scale-110 transition-transform duration-300">
          <PokeverseIconAnimated size={80} />
        </div>
        <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 bg-clip-text text-transparent drop-shadow-lg">
          Pokéverse
        </h3>
        <p className="text-white/70 text-sm mt-1 h-5 drop-shadow">
          {typingText}
          <span className="animate-pulse">|</span>
        </p>
      </div>
      
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/15 to-pink-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl" />
      
      <style>{`
        @keyframes twinkleStar {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
      `}</style>
    </Link>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currency } = useCurrencyStore();
  const [searchQuery, setSearchQuery] = useState(() => {
    return location.state?.restoreSearch ? localStorage.getItem(SEARCH_HISTORY_KEY) || '' : '';
  });
  const [searchResults, setSearchResults] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCards, setTotalCards] = useState<number>(0);
  const [totalSets, setTotalSets] = useState<number>(0);
  const [totalSeries, setTotalSeries] = useState<number>(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [valuableCards, setValuableCards] = useState<Card[]>([]);
  const [valuableCardsError, setValuableCardsError] = useState<string | null>(null);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [likedCards, setLikedCards] = useState<Card[]>([]);
  const [likedCardsError, setLikedCardsError] = useState<string | null>(null);
  const [likedCardsLoading, setLikedCardsLoading] = useState(true);
  const [latestSets, setLatestSets] = useState<Array<{ name: string, total: number, images?: { logo?: string } }>>([]);
  const [error, setError] = useState<string | null>(null);

  const placeholderText = useTypingEffect([
    "Search for 'Charizard'...",
    "Search for 'Pikachu'...",
    "Search for 'Mewtwo'...",
    "Search for 'Umbreon'...",
    "Search for 'Rayquaza'...",
  ], 100, 50, 2000);

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const sets = await getAllSets();
        if (!sets) {
          setError('Unable to load card data. Please try again later.');
          return;
        }
        setTotalSets(sets.length);
        const uniqueSeries = new Set(sets.map(set => set.series));
        setTotalSeries(uniqueSeries.size);
        const cardCount = sets.reduce((acc, set) => acc + set.total, 0);
        setTotalCards(cardCount);
        const latest = sets
          .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
          .slice(0, 4);
        setLatestSets(latest);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Unable to load card data. Please try again later.');
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchValuableCards = async () => {
      setFeaturedLoading(true);
      setValuableCardsError(null);
      try {
        const cards = await getMostValuableCards(10);
        if (cards && cards.length > 0) {
          setValuableCards(cards);
        } else {
          setValuableCardsError('No valuable cards found');
        }
      } catch (error) {
        console.error('Error fetching valuable cards:', error);
        setValuableCardsError('Failed to load valuable cards');
      } finally {
        setFeaturedLoading(false);
      }
    };

    fetchValuableCards();
  }, []);

  useEffect(() => {
    const fetchLikedCards = async () => {
      setLikedCardsLoading(true);
      setLikedCardsError(null);
      try {
        const cards = await getMostLikedCards(6, 'most_liked', 'all');
        if (cards && cards.length > 0) {
          setLikedCards(cards);
        } else {
          setLikedCardsError('No voted cards found yet');
        }
      } catch (error) {
        console.error('Error fetching liked cards:', error);
        setLikedCardsError('Failed to load community favorites');
      } finally {
        setLikedCardsLoading(false);
      }
    };

    fetchLikedCards();
  }, []);

  useEffect(() => {
    if (location.state?.restoreSearch && searchQuery) {
      performSearch(searchQuery);
    }
  }, [location.state]);

  const performSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSearchResults([]);
        return;
      }
      setLoading(true);
      try {
        const results = await searchAllCards(query);
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    localStorage.setItem(SEARCH_HISTORY_KEY, query);
    performSearch(query);
  };

  const handleCardClick = (card: Card) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}/`, {
      state: { fromSearch: true }
    });
  };

  // Show error state if there's an error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Unable to Load</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-8">
      <Helmet>
        <title>PokeCardIndex - Comprehensive Pokémon TCG Database</title>
        <meta name="description" content="Explore the complete Pokémon Trading Card Game database. Search cards, view prices, track collections, and discover the most valuable Pokémon cards. Browse over 18,000 cards across 164 sets - 100% free!" />
        <meta name="keywords" content="pokemon cards, pokemon card database, pokemon tcg, trading card game, card prices, card collection, pokemon card values, pokemon card index" />
        <meta property="og:title" content="PokeCardIndex - Comprehensive Pokémon TCG Database" />
        <meta property="og:description" content="Explore the complete Pokémon Trading Card Game database. Search cards, view prices, track collections, and discover the most valuable Pokémon cards." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pokecardindex.com/" />
        <link rel="canonical" href="https://pokecardindex.com/" />
      </Helmet>
      <div className="max-w-[1600px] mx-auto">
        <div className="text-center mb-12 sm:mb-24">
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
           The Pokémon Card Database - PokeCardIndex
          </h1>
          <p className="text-base sm:text-xl text-white/60 mb-4 sm:mb-6" itemProp="description">
            Your comprehensive resource for Pokémon card information, prices, and collection tracking.
            Browse over {totalCards?.toLocaleString() || '18,000'} cards across {totalSets || '164'} sets.
          </p>
          <div className="text-base sm:text-lg text-green-500 font-medium animate-pulse mt-4 sm:mt-8">
            100% Free - No Premium Features or Paywalls!
          </div>
          <p className="text-base sm:text-lg text-white/60 mt-2">
            Powered by{' '}
            <a 
              href="https://boxed.gg/pokemon?utm_source=pokecardindex"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors"
            >
              BOXED.gg
            </a>
            <span className="animate-heartbeat text-red-500">♥</span>
          </p>
        </div>

        <div className="relative mb-8 sm:mb-16">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={placeholderText}
            className="w-full bg-white/5 text-white px-4 sm:px-6 py-4 sm:py-5 rounded-xl border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/25 pl-12 sm:pl-14 placeholder-white/40 text-base sm:text-lg"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
        </div>

        {searchQuery ? (
          <SearchResults cards={searchResults} loading={loading} />
        ) : (
          <>
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-4 sm:p-6 mb-12 sm:mb-24">
              {/* Mobile Layout */}
              <div className="lg:hidden space-y-4">
                {/* Database Overview - Mobile */}
                <div>
                  <h2 className="text-lg font-bold mb-3">Database Overview</h2>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10 text-center">
                      <h3 className="text-white/60 text-[10px] sm:text-xs mb-0.5">Cards</h3>
                      <p className="text-sm sm:text-lg font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalCards.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10 text-center">
                      <h3 className="text-white/60 text-[10px] sm:text-xs mb-0.5">Sets</h3>
                      <p className="text-sm sm:text-lg font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalSets}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10 text-center">
                      <h3 className="text-white/60 text-[10px] sm:text-xs mb-0.5">Series</h3>
                      <p className="text-sm sm:text-lg font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalSeries}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3 border border-white/10 text-center">
                      <h3 className="text-white/60 text-[10px] sm:text-xs mb-0.5">Updates</h3>
                      <p className="text-sm sm:text-lg font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        Daily
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Pokéverse - Mobile (full width) */}
                <PokeverseBanner />
                
                {/* Latest Sets - Mobile */}
                <div>
                  <h3 className="text-lg font-bold mb-3">Latest Sets</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {statsLoading ? (
                      Array(4).fill(0).map((_, i) => (
                        <div key={i} className="animate-pulse bg-white/5 rounded-lg p-3 h-16"></div>
                      ))
                    ) : (
                      latestSets.slice(0, 4).map((set, index) => (
                        <Link
                          key={index}
                          to={`/scarlet-violet/${slugify(set.name)}/`}
                          className="relative flex flex-col items-center justify-center bg-white/5 rounded-lg p-2 hover:bg-white/10 transition-colors border border-white/10 overflow-hidden group min-h-[70px]"
                        >
                          {set.images?.logo && (
                            <div 
                              className="absolute inset-0 opacity-60 transition-opacity group-hover:opacity-80"
                              style={{
                                backgroundImage: `url(${set.images.logo})`,
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                              }}
                            />
                          )}
                          <div className="relative z-10 text-center">
                            <h4 className="font-medium text-xs leading-tight">{set.name}</h4>
                            <p className="text-white/60 text-[10px]">{set.total} cards</p>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
              
              {/* Desktop Layout */}
              <div className="hidden lg:block">
                {/* Titles row */}
                <div className="grid grid-cols-3 gap-6 mb-4">
                  <h2 className="text-xl font-bold">Database Overview</h2>
                  <div /> {/* Spacer for Pokéverse */}
                  <h3 className="text-lg font-bold">Latest Sets</h3>
                </div>
                
                {/* Content row - aligned boxes */}
                <div className="grid grid-cols-3 gap-6">
                  {/* Database Overview Stats */}
                  <div className="grid grid-cols-2 gap-3 content-start">
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <h3 className="text-white/60 text-xs mb-1">Cards</h3>
                      <p className="text-xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalCards.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <h3 className="text-white/60 text-xs mb-1">Sets</h3>
                      <p className="text-xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalSets}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <h3 className="text-white/60 text-xs mb-1">Series</h3>
                      <p className="text-xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        {statsLoading ? <span className="animate-pulse">...</span> : totalSeries}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <h3 className="text-white/60 text-xs mb-1">Updates</h3>
                      <p className="text-xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                        Daily
                      </p>
                    </div>
                  </div>

                  {/* Pokéverse Button - Center */}
                  <PokeverseBanner />

                  {/* Latest Sets */}
                  <div className="space-y-2">
                    {statsLoading ? (
                      Array(3).fill(0).map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-4 bg-white/10 rounded w-3/4 mb-1"></div>
                          <div className="h-3 bg-white/10 rounded w-1/4"></div>
                        </div>
                      ))
                    ) : (
                      latestSets.slice(0, 3).map((set, index) => (
                        <Link
                          key={index}
                          to={`/scarlet-violet/${slugify(set.name)}/`}
                          className="relative flex items-center gap-3 bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors border border-white/10 overflow-hidden group"
                        >
                          {set.images?.logo && (
                            <div 
                              className="absolute inset-0 opacity-50 transition-opacity group-hover:opacity-75"
                              style={{
                                backgroundImage: `url(${set.images.logo})`,
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                              }}
                            />
                          )}
                          <div className="relative z-10">
                            <h4 className="font-medium text-sm">{set.name}</h4>
                            <p className="text-white/60 text-xs">{set.total} cards</p>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-12 sm:mb-24">
              <div className="group bg-gradient-to-br from-blue-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl">
                    <FeatureIcons.Database />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Extensive Database</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Access over {totalCards?.toLocaleString()} cards across all sets with high-quality images and detailed information.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-yellow-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-yellow-500/10 rounded-xl">
                    <FeatureIcons.DollarSign />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Real-time Prices</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Track market values updated daily to make informed collecting decisions.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-green-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-green-500/10 rounded-xl">
                    <FeatureIcons.BookOpen />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Collection Tracking</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Manage and organize your cards with our powerful collection tools.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-purple-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-purple-500/10 rounded-xl">
                    <FeatureIcons.BarChart />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Advanced Analytics</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Track collection value and completion with detailed analytics.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-red-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-red-500/10 rounded-xl">
                    <FeatureIcons.Zap />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">100% Free</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  No Premium Features or Paywalls! Everything is completely free.
                </p>
              </div>
              
              <div className="group bg-gradient-to-br from-orange-950/50 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 hover:border-white/20 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-orange-500/10 rounded-xl">
                    <FeatureIcons.Rocket />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold">Fast Search</h3>
                </div>
                <p className="text-white/60 text-sm sm:text-base">
                  Find any card instantly with our powerful search engine.
                </p>
              </div>
            </div>

            <div className="mt-12 sm:mt-24 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Start Your Collection Today</h2>
              <p className="text-base sm:text-lg text-white/60 mb-8 sm:mb-12 max-w-2xl mx-auto">
                Join thousands of collectors using PokeCardIndex to track their collections.
                100% Free - Join our Discord community to connect with other collectors.
              </p>
              <Link
                to="/auth?register=true"
                className="inline-flex items-center gap-3 px-8 sm:px-10 py-3 sm:py-4 bg-white/10 hover:bg-white/20 rounded-xl text-white font-medium transition-all duration-300 hover:scale-105 text-base sm:text-lg"
              >
                Create Free Account
                <Zap className="w-5 h-5" />
              </Link>
            </div>

            {/* Most Liked Cards by Community */}
            {!likedCardsLoading && (
              <div className="mt-12 sm:mt-24">
                <div className="bg-gradient-to-br from-pink-950/50 to-transparent border border-pink-500/20 p-4 sm:p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">❤️</span>
                      <h2 className="text-xl font-semibold">Community Favorites</h2>
                    </div>
                    <div className="text-sm text-pink-400 bg-pink-500/10 px-2 py-1 rounded-full">
                      Most Liked
                    </div>
                    </div>
                    <Link
                      to="/community-sentiment"
                      className="text-sm text-pink-400 hover:text-pink-300 transition-colors flex items-center gap-1"
                    >
                      View All <span>→</span>
                    </Link>
                  </div>
                  <p className="text-white/60 text-sm mb-4">
                    Cards with the most positive sentiment from PokeCardIndex users
                  </p>
                  {likedCardsError ? (
                    <div className="text-center py-8 text-white/60">
                      <p className="mb-2">Unable to load community favorites</p>
                      <p className="text-sm">{likedCardsError}</p>
                    </div>
                  ) : likedCards.length === 0 ? (
                    <div className="text-center py-8 text-white/60">
                      <div className="text-6xl mb-4">🗳️</div>
                      <p className="text-lg mb-2">No community favorites yet!</p>
                      <p className="text-sm">Be the first to like cards and share your favorites with the community.</p>
                      <p className="text-xs mt-2 text-pink-400">Visit any card page and click the heart button to like it!</p>
                      <Link 
                        to="/community-sentiment" 
                        className="inline-block mt-3 text-sm text-pink-400 hover:text-pink-300 transition-colors"
                      >
                        Explore Community Sentiment →
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                      {likedCards.map((card: any, index) => (
                      <article
                        key={card.id}
                        className={`group relative bg-black/50 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-pink-400/50 transition-all shadow-lg ${index === 5 ? 'lg:hidden' : ''}`}
                        onClick={() => handleCardClick(card)}
                      >
                        <div className="relative aspect-[2.5/3.5]">
                                                      <CardImage
                            src={card.images.small}
                            alt={card.name}
                            cardId={card.id}
                            className="absolute inset-0 w-full h-full object-cover"
                            width={245}
                            quality={75}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                            <div className="absolute bottom-0 inset-x-0 p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div>
                                  <p className="text-sm font-medium text-white truncate">{card.name}</p>
                                  <p className="text-xs text-white/60">
                                    {card.number} • {card.set.name}
                                  </p>
                                </div>
                                {card.types?.[0] && (
                                  <EnergyIcon type={card.types[0]} />
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-pink-400">
                                  ❤️ {card.vote_stats?.likes_count || 0}
                                </span>
                                <span className="text-xs text-white/60">
                                  ({(card.vote_stats?.total_primary_votes || 0) + (card.vote_stats?.total_reactions || 0)} total reactions)
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!valuableCardsError && !featuredLoading && valuableCards.length > 0 && (
              <div className="mt-12 sm:mt-24">
                <div className="bg-white/5 border border-white/10 p-4 sm:p-6 rounded-xl">
                  <h2 className="text-xl font-semibold mb-4">Most Valuable Cards</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {valuableCards.map((card, index) => (
                      <article
                        key={card.id}
                        className="group relative bg-black/50 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/25 transition-all shadow-lg"
                        onClick={() => handleCardClick(card)}
                      >
                        <div className="relative aspect-[2.5/3.5]">
                          <CardImage
                            src={card.images.small}
                            alt={card.name}
                            cardId={card.id}
                            className="absolute inset-0 w-full h-full object-cover"
                            width={245}
                            quality={75}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                            <div className="absolute bottom-0 inset-x-0 p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div>
                                  <p className="text-sm font-medium text-white truncate">{card.name}</p>
                                  <p className="text-xs text-white/60">
                                    {card.number}/{card.set.printedTotal}
                                  </p>
                                </div>
                                {card.types?.[0] && (
                                  <EnergyIcon type={card.types[0]} />
                                )}
                              </div>
                              <p className="text-sm font-medium text-white/80">
                                {formatPrice(getCardPrice(card), currency)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}